
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, OnDestroy, OnInit, ChangeDetectorRef, HostListener, AfterViewInit, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { RootState, getIsDeviceReady, getOnlineStatus, getUserProfile } from "./reducers/index";
import { DeviceReady, SetOnlineStatus, WindowFocusStatus } from "./actions/app";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { ConfigService } from "./common/providers/config.service";
import { AppConstants } from "./common/utils/app-constants";
import { TasksConstants } from "./task/shared/task-constacts";
import { RouterOutlet } from "@angular/router";
import { Broadcaster } from "./common/providers/broadcaster.service";
import { MessageTranslatorService } from "./task/services";
import { environment } from "../environments/environment";
import { MatIconRegistry } from "@angular/material/icon";
import { takeWhile, map, filter, take } from "rxjs/operators";
import { fromEvent, merge } from "rxjs";
import { ElectronService } from "./task/services/electron.service";
import { CommonUtil } from "./common/utils/common.utils";
import { VNCActionWheelMenuComponent, VNCActionWheelMenuService, VncLibraryService } from "@uxf/vnc-library";
import { TasksRootState } from "../app/task/store";
import { ResponsiveService } from "./common/providers/responsive.service";
import { StorageService } from "./common/services/storage.service";
import { BackButtonService } from "./common/providers/back-button.service";
import { DeepLinkService } from "./common/providers/deep-link.service";
import { WindowMessageService } from "./common/providers/window-message.service";
import { ContactActionsService } from "./common/providers/contact-actions.service";

/**
 * Application shell. It owns platform wiring only - language, device readiness, online
 * status, window focus and the two host listeners. The blocks it used to inline are now
 * services it delegates to (STR-05):
 *
 *   - BackButtonService     Android back-button dialog stack
 *   - DeepLinkService       vnctask: URLs, universal links, share sheet
 *   - WindowMessageService  window "message" traffic from the auth iframes
 *   - ContactActionsService contact wheel menu cross-app actions
 *
 * Offline replay used to live here too; the repository owns it now (STR-06).
 */
@Component({
    selector: "vp-app",
    template: `
    <router-outlet></router-outlet>
    @if (!isMobileDevice) {
      <vnc-action-wheel-menu
        middleLabel="{{ 'PROFILE_INFO' | translate }}"
        label1="{{ 'START_AUDIO_CALL' | translate }}"
        label2="{{ 'START_CHAT' | translate }}"
        label3="{{ 'START_VIDEO_CALL' | translate }}"
        label4="{{ 'COMPOSE_MAIL' | translate }}"
        label5="{{ 'CREATE_TICKET' | translate }}"
        label6="{{ 'CREATE_TASK' | translate }}"
        (actionClicked)="handleActionClick($event)"
      style="display: none"></vnc-action-wheel-menu>
    }
    `,
    imports: [RouterOutlet, VNCActionWheelMenuComponent, TranslatePipe]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private rootStore = inject<Store<RootState>>(Store);
  private changeDetectionRef = inject(ChangeDetectorRef);
  private translate = inject(TranslateService);
  configService = inject(ConfigService);
  private broadcaster = inject(Broadcaster);
  private messageTranslatorService = inject(MessageTranslatorService);
  private matIconRegistry = inject(MatIconRegistry);
  private electronService = inject(ElectronService);
  private actionWheelMenuService = inject(VNCActionWheelMenuService);
  private store = inject<Store<TasksRootState>>(Store);
  private responsiveService = inject(ResponsiveService);
  private vncLibaryService = inject(VncLibraryService);
  private backButtonService = inject(BackButtonService);
  private deepLinkService = inject(DeepLinkService);
  private windowMessageService = inject(WindowMessageService);
  private contactActionsService = inject(ContactActionsService);

  isAlive: boolean = true;
  isOnline: boolean = false;
  isCordovaOrElectron = environment.isCordova || environment.isElectron;
  user: any = {};
  isMobileDevice = CommonUtil.isOnMobileDevice();

  constructor() {
    this.matIconRegistry.registerFontClassAlias("mdi");
    let browserLang = this.translate.getBrowserLang();
    let localLang = StorageService.getLocalItem(AppConstants.TASK_LANGUAGE);
    if (localLang !== null && localLang !== "undefined") {
      browserLang = localLang;
    }
    let selectedLanguage = browserLang.match(/en|ru|de/) ? browserLang : "en";
    this.translate.use(selectedLanguage);
    this.translate.reloadLang(selectedLanguage);
    this.configService.language = selectedLanguage;
    this.checkInternetConnection();
    this.checkWindowFocused();

    this.broadcaster.on<boolean>("OFFLINE_CONNECTION")
    .subscribe(data => {
      const message = this.messageTranslatorService.getMessage(TasksConstants.OFFLINE);
      this.vncLibaryService.openSnackBar(message, "","", "", 3000, "bottom", "left").subscribe(res => {
      });
      this.changeDetectionRef.markForCheck();
    });
  }

  ngOnInit() {
    this.actionWheelMenuService.getData().subscribe(v => {
      console.log("[AppComponent] actionWheelMenuService", v);
    });
    document.addEventListener("deviceready", this.onDeviceReady.bind(this));

    this.rootStore.select(getIsDeviceReady).pipe(filter(isReady => isReady), take(1)).subscribe( isReady => {
      this.setupNativeChrome();
      this.setupNativeIntegrations();
    });

    this.responsiveService.screen$
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(screen => {
        this.isMobileDevice = CommonUtil.isOnMobileDevice();
        this.changeDetectionRef.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.rootStore.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      if (!navigator.onLine) {
        let message = "";
        if (StorageService.getLocalItem(AppConstants.TASK_LANGUAGE) === "de") {
          message = "Verbindung zum Internet unterbrochen";
        } else {
          message = "You lost internet connection";
        }
        this.vncLibaryService.openSnackBar(message, "","", "", 3000, "bottom", "left").subscribe(res => {
        });
      }
      this.changeDetectionRef.markForCheck();
    });
    this.store.select(getUserProfile).pipe(takeWhile(() => this.isAlive)).subscribe(user => {
      if (user) {
        this.user = user;
        setTimeout( () => { this.changeDetectionRef.markForCheck(); }, 100);
      }
    });
  }

  ngOnDestroy() {
    this.isAlive = false;

    this.broadcaster.broadcast("appComponentOnDestroy");
  }

  /** Splash screen, keyboard, orientation lock and status bar - native shell chrome. */
  private setupNativeChrome(): void {
    if (typeof navigator !== "undefined" && navigator.splashscreen) {
      navigator.splashscreen.hide();
      this.changeDetectionRef.detectChanges();
    }
    if (typeof Keyboard !== "undefined") {
      Keyboard.shrinkView(true);
    }

    if (CommonUtil.isOnMobileDevice() && !CommonUtil.isOnIpad() && screen) {
      (screen.orientation as any).lock("portrait");
    } else {
      screen.orientation.unlock();
    }

    if (environment.isCordova && StatusBar) {
      console.log("[AppComponent][onDeviceReady] StatusBar.show");
      StatusBar.show();
      if (CommonUtil.isOnIOS()) {
        StatusBar.backgroundColorByHexString("#0070BC");
        StatusBar.styleLightContent();
      }
    }
  }

  /** Universal links, the Cordova connection events and the OS share sheet. */
  private setupNativeIntegrations(): void {
    if (universalLinks) {
      universalLinks.subscribe("launchedAppFromLink", eventData => this.deepLinkService.onApplicationDidLaunchFromLink(eventData));
      universalLinks.subscribe("triggerActionEvent", eventData => this.deepLinkService.processTriggerActionEvent(eventData));
    }

    document.addEventListener("online", this.handleConnectionChange.bind(this), false);
    document.addEventListener("offline", this.handleConnectionChange.bind(this), false);

    if (!!cordova?.openwith) {
      const initSuccess = () => {
        console.log("[openwith] init success!");
      };
      const initError = (err) => {
        console.log("[openwith] init failed: " + err);
      };
      cordova.openwith.init(initSuccess, initError);
      const myHandler = (intent) => {
        console.log("[openwith] intent received", intent);
        StorageService.setLocalItem("shared_data", JSON.stringify({
          intent: intent
        }));
        this.deepLinkService.handleSharing();
      };
      cordova.openwith.addHandler(myHandler);
    }
  }

  private checkInternetConnection(): void {
    if (!environment.isCordova) {
      window.addEventListener("online", this.handleConnectionChange.bind(this));
      window.addEventListener("offline", this.handleConnectionChange.bind(this));
    } else {
      this.rootStore.dispatch(new SetOnlineStatus(navigator.onLine));
    }
  }

  private checkWindowFocused() {
    merge(
      fromEvent(window , "focus").pipe(map(() => true)) ,
      fromEvent(window , "blur").pipe(map(() => false)))
      .subscribe((focused) => {
        this.rootStore.dispatch(new WindowFocusStatus(focused));
    });
  }

  private handleConnectionChange(event): void {
    console.log("NETWORK connection status is now: ", event);
    if (environment.isElectron) {
      this.rootStore.select(getOnlineStatus).pipe(take(1)).subscribe(isOnline => {
        if (isOnline !== navigator.onLine) {
          setTimeout(() => {
            this.rootStore.dispatch(new SetOnlineStatus(navigator.onLine));
          }, 5000);
        } else {
          this.rootStore.dispatch(new SetOnlineStatus(navigator.onLine));
        }
      });
    } else {
      this.rootStore.dispatch(new SetOnlineStatus(navigator.onLine));
    }
    // The pending-operation replay used to run here as well, racing the repository's own
    // replay and its server pull. The repository now owns it: SetOnlineStatus above is
    // what drives initRepo, which replays both queues before pulling. (STR-06)
  }

  onDeviceReady() {
    console.log("[App Component] Device Ready");
    this.rootStore.dispatch(new DeviceReady());
    this.deepLinkService.setupHandleOpenURL();

    if (window["cordova"].InAppBrowser) {
      window.open = window["cordova"].InAppBrowser.open;
    }

    document.addEventListener("backbutton", () => {
      this.backButtonService.handleBackButton();
      console.log("[AppComponent] onBackButton", "end");
      this.onPushBugHack();
    }, false);
  }

  @HostListener("window:message", ["$event"])
  windowMessageEventHandler(event: MessageEvent) {
    // Origin is checked again inside the service; keeping it here means an untrusted
    // message is dropped before it reaches any handler (XSS-06).
    if (!CommonUtil.isTrustedMessageEvent(event)) {
      console.warn("[AppComponent][windowMessageEventHandler] ignored message from untrusted origin", event.origin);
      return;
    }
    this.windowMessageService.handleMessage(event, this.isOnline, this.changeDetectionRef);
  }

  private onPushBugHack() {
    setTimeout(() => {
    this.changeDetectionRef.detectChanges();
    }, 100);
  }

  @HostListener("document:click", ["$event"])
  documentClickEventHandler(event: any) {
    if (!this.isCordovaOrElectron) {
      return;
    }
    let url: string;
    if (event.target.classList.contains("open-new-window")) {
      url = event.target.href;
    }

    this.actionWheelMenuService.hideCircularMenu();
    if (url) {
      event.stopPropagation();
      event.preventDefault();
      console.log("[documentClickEventHandler]", event);
      if (this.electronService.isElectron) {
        this.electronService.openExternalUrl(url);
      } else if (typeof device !== "undefined" && device.platform === "iOS") {
        window.open(url, "_system");
      } else if (typeof device !== "undefined" && device.platform === "Android") {
        navigator.app.loadUrl(url, {
          openExternal: true
        });
      }
    }
  }

  handleActionClick(v) {
    this.contactActionsService.handleActionClick(v, this.user, this.changeDetectionRef);
  }
}
